import { useEffect, useRef, useState } from 'react';
import { SCROLLSTEP } from '../../../constants/Constants';
import { WineWidgetModel } from '../../../models/WineWidgetModel';

interface useScrollProps {
  wineList: WineWidgetModel[];
  numberOfRenderedWines: number;
}
export const useScroll = ({ wineList, numberOfRenderedWines }: useScrollProps) => {
  // ref for the packages component
  const scrollbarRef = useRef<HTMLDivElement>(null);
  // ref for the container of the packages
  const scrollbarContainerRef = useRef<HTMLDivElement>(null);
  // true if there is a scrollbar
  const [isScrollbarVisible, setIsScrollbarVisible] = useState<boolean>(false);
  const [isScrollableToLeft, setIsScrollableToLeft] = useState(false);
  const [isScrollableToRight, setIsScrollableToRight] = useState(true);

  // if the wines changed, check if the scrollbarRef.current.scrollWidth is bigger than the containers width or not
  useEffect(() => {
    if (!scrollbarRef.current || !scrollbarContainerRef.current) return;
    if (scrollbarRef.current?.scrollWidth <= scrollbarContainerRef.current?.scrollWidth) setIsScrollbarVisible(false);
    if (scrollbarRef.current?.scrollWidth > scrollbarContainerRef.current?.scrollWidth) setIsScrollbarVisible(true);
  }, [wineList, scrollbarRef, scrollbarContainerRef, numberOfRenderedWines]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollbarRef.current && scrollbarContainerRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = scrollbarRef.current;
        const isScrollableToLeft = scrollLeft > 0;
        const isScrollableToRight = scrollWidth > clientWidth + scrollLeft + 2;

        setIsScrollableToLeft(isScrollableToLeft);
        setIsScrollableToRight(isScrollableToRight);
      }
    };

    if (scrollbarRef.current) {
      scrollbarRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollbarRef.current) {
        scrollbarRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [scrollbarRef, scrollbarContainerRef]);

  // smooth scroll animation on buttons
  const scrollSmoothly = (scrollOffset: number) => {
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollTo({
        left: scrollbarRef.current.scrollLeft + scrollOffset,
        behavior: 'smooth',
      });
    }
  };

  // scroll left function by given scroll step
  const scrollLeft = () => {
    scrollSmoothly(-SCROLLSTEP);
  };

  // scroll right function by given scroll step
  const scrollRight = () => {
    scrollSmoothly(SCROLLSTEP);
  };

  return { scrollbarRef, scrollLeft, scrollRight, isScrollbarVisible, scrollbarContainerRef, isScrollableToLeft, isScrollableToRight };
};
